import { useSyncExternalStore } from 'react'

/**
 * Workspace timezone for the current session.
 *
 * Every stored timestamp is UTC; this decides how the UI renders them and is
 * the default for new schedules. There is one timezone per workspace, set by
 * the tenant owner; there is no per-user timezone. Unset falls back to UTC.
 * `setTimezoneSources` is fed from the auth store by `stores/timezoneSync.ts`;
 * nothing else should write it.
 */

export function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz) return false
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export interface TimezoneSources {
  tenant?: string | null
}

/** 'tenant' = the workspace timezone is set; 'default' = unset, so UTC. */
export type TimezoneSource = 'tenant' | 'default'

export const DEFAULT_WORKSPACE_TIMEZONE = 'UTC'

/** Pure resolver, exported for tests. */
export function resolveTimezone(sources: TimezoneSources): { timezone: string; source: TimezoneSource } {
  if (isValidTimeZone(sources.tenant)) return { timezone: sources.tenant, source: 'tenant' }
  return { timezone: DEFAULT_WORKSPACE_TIMEZONE, source: 'default' }
}

let current = resolveTimezone({})
const listeners = new Set<() => void>()

export function setTimezoneSources(sources: TimezoneSources): void {
  const next = resolveTimezone(sources)
  if (next.timezone === current.timezone && next.source === current.source) return
  current = next
  listeners.forEach(l => l())
}

/** The IANA timezone every timestamp should currently render in. */
export function getActiveTimezone(): string {
  return current.timezone
}

export function getActiveTimezoneSource(): TimezoneSource {
  return current.source
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Subscribe a component to timezone changes so it re-renders (and re-formats)
 * when the owner edits the workspace timezone in settings. Components that merely call
 * `formatTimestamp` during render do not need this unless they are memoised.
 */
export function useActiveTimezone(): string {
  return useSyncExternalStore(subscribe, getActiveTimezone, getActiveTimezone)
}

/** True once the tenant owner has chosen a workspace timezone (false = UTC fallback). */
export function useWorkspaceTimezoneSet(): boolean {
  return useSyncExternalStore(subscribe, isWorkspaceTimezoneSet, isWorkspaceTimezoneSet)
}

function isWorkspaceTimezoneSet(): boolean {
  return current.source === 'tenant'
}

/**
 * A schedule form's zone list with the workspace timezone guaranteed present,
 * first. New schedules default to the workspace timezone but may override it,
 * and a short hard-coded list must not silently drop the default.
 */
export function withWorkspaceTimezone(zones: readonly string[], workspace: string = getActiveTimezone()): string[] {
  return zones.includes(workspace) ? [...zones] : [workspace, ...zones]
}

/** Short zone label for the active timezone, e.g. "IST", "EST", "GMT+5:30". */
export function getTimezoneAbbr(date: Date = new Date(), timeZone: string = getActiveTimezone()): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'short' }).formatToParts(date)
    return parts.find(p => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

/** All selectable IANA zones; falls back to a short list on runtimes without supportedValuesOf. */
export function listTimezones(): string[] {
  const fallback = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo', 'Europe/London', 'Europe/Berlin', 'Africa/Johannesburg',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
  ]
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] }
  let zones: string[]
  try {
    zones = intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : fallback
  } catch {
    zones = fallback
  }
  // Engines list some pre-rename aliases next to the current name ("Asia/Calcutta" and "Asia/Kolkata");
  // showing both makes people think they are different zones.
  const legacy: Record<string, string> = {
    'Asia/Calcutta': 'Asia/Kolkata',
    'Asia/Katmandu': 'Asia/Kathmandu',
    'Asia/Saigon': 'Asia/Ho_Chi_Minh',
    'Asia/Rangoon': 'Asia/Yangon',
    'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
    'Europe/Kiev': 'Europe/Kyiv',
  }
  // Some engines (Chrome) list only the old name, so swap rather than drop; Intl accepts both spellings.
  zones = [...new Set(zones.map(z => legacy[z] ?? z))].sort()
  // supportedValuesOf omits "UTC" on some engines; every picker needs it.
  return zones.includes('UTC') ? zones : ['UTC', ...zones]
}

/**
 * Install the active timezone as the default `timeZone` for
 * Date#toLocaleString / toLocaleDateString / toLocaleTimeString.
 *
 * This is how the ~220 legacy call sites (and chart/table libraries) honour the
 * workspace timezone without being rewritten one by one. An explicit `timeZone` in
 * the caller's options always wins, so code that deliberately formats in UTC or
 * a schedule's own zone is untouched. Number#toLocaleString is a different
 * prototype and is not affected.
 *
 * Call once at app start; calling again is a no-op. New code should prefer the
 * helpers in `formatTimestamp.ts`, which pass the zone explicitly.
 */
let patched = false
export function installDateTimezoneDefaults(): void {
  if (patched) return
  patched = true
  const methods = ['toLocaleString', 'toLocaleDateString', 'toLocaleTimeString'] as const
  for (const method of methods) {
    const original = Date.prototype[method]
    Date.prototype[method] = function (
      this: Date,
      locales?: Intl.LocalesArgument,
      options?: Intl.DateTimeFormatOptions,
    ): string {
      if (options?.timeZone) return original.call(this, locales, options)
      return original.call(this, locales, { ...options, timeZone: getActiveTimezone() })
    }
  }
}
